import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { MessageCircle, Phone, Mail, RefreshCw, Sparkles, X } from 'lucide-react';
import api from '../../lib/api';

const STATUS_CONFIG = {
  new: { label: 'New', className: 'bg-[#FF007F]/20 text-[#FF007F] border-[#FF007F]/40' },
  contacted: { label: 'Contacted', className: 'bg-blue-500/20 text-blue-400 border-blue-500/40' },
  trial_attended: { label: 'Trial Attended', className: 'bg-amber-500/20 text-amber-400 border-amber-500/40' },
  joined: { label: 'Joined UPA', className: 'bg-green-500/20 text-green-400 border-green-500/40' },
  not_interested: { label: 'Not Interested', className: 'bg-neutral-500/20 text-neutral-400 border-neutral-500/40' },
};

const formatSubmitted = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

export const RecentTrialEnquiriesPanel = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEnquiries = async () => {
    try {
      const res = await api.get('/free-trials/recent-enquiries');
      setEnquiries(res.data.enquiries || []);
    } catch (err) {
      console.error('Failed to load trial enquiries:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEnquiries(); }, []);

  const updateStatus = async (id, status, silent = false, method = null) => {
    // Optimistic update
    setEnquiries((prev) => prev.map((e) => (e.id === id ? { ...e, lead_status: status } : e)));
    try {
      await api.patch(`/free-trials/${id}/lead-status`, method ? { status, method } : { status });
      if (!silent) toast.success(`Marked as ${STATUS_CONFIG[status]?.label || status}`);
    } catch (err) {
      toast.error('Failed to update status');
      fetchEnquiries();
    }
  };

  const dismissFromOverview = async (id) => {
    setEnquiries((prev) => prev.filter((e) => e.id !== id));
    try {
      await api.patch(`/free-trials/${id}/dismiss-overview`);
      toast.success('Removed from overview — still available in the Free Trials tab');
    } catch (err) {
      toast.error('Failed to remove enquiry');
      fetchEnquiries();
    }
  };

  const handleWhatsApp = (enquiry) => {
    window.open(enquiry.whatsapp_link, '_blank', 'noopener');
    if (enquiry.lead_status === 'new') {
      updateStatus(enquiry.id, 'contacted', true, 'whatsapp');
      toast.success('Opened WhatsApp — marked as Contacted');
    }
  };

  return (
    <Card className="bg-[#111] border-[#333]" data-testid="recent-trial-enquiries-panel">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-white flex items-center gap-2 text-base">
          <Sparkles className="w-4 h-4 text-[#FF007F]" />
          Recent Free Trial Enquiries
          {enquiries.filter((e) => e.lead_status === 'new').length > 0 && (
            <Badge className="bg-[#FF007F] text-white border-0" data-testid="new-enquiries-count">
              {enquiries.filter((e) => e.lead_status === 'new').length} new
            </Badge>
          )}
        </CardTitle>
        <button onClick={fetchEnquiries} className="text-white/40 hover:text-white" data-testid="refresh-enquiries-btn">
          <RefreshCw className="w-4 h-4" />
        </button>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <p className="text-white/40 text-sm">Loading enquiries...</p>
        ) : enquiries.length === 0 ? (
          <p className="text-white/40 text-sm">No trial enquiries yet.</p>
        ) : (
          enquiries.map((e) => (
            <div key={e.id} className="bg-[#1a1a1a] border border-[#333] rounded-lg p-3" data-testid={`enquiry-${e.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-white font-medium text-sm">
                    {e.student_name}
                    {e.student_age != null && <span className="text-white/50"> · {e.student_age} yrs</span>}
                  </p>
                  <p className="text-white/60 text-xs mt-0.5">
                    {e.class_name} · Trial: {e.trial_date || 'TBC'}
                  </p>
                  <p className="text-white/60 text-xs mt-0.5">
                    Parent: {e.parent_name} · Submitted {formatSubmitted(e.created_at)}
                  </p>
                  {e.notes && <p className="text-white/40 text-xs mt-0.5">Notes: {e.notes}</p>}
                  <div className="flex items-center gap-3 mt-1.5 text-xs">
                    {e.parent_phone && (
                      <a href={`tel:${e.parent_phone}`} className="text-white/70 hover:text-white flex items-center gap-1">
                        <Phone className="w-3 h-3" />{e.parent_phone}
                      </a>
                    )}
                    {e.parent_email && (
                      <a href={`mailto:${e.parent_email}`} className="text-white/70 hover:text-white flex items-center gap-1 truncate">
                        <Mail className="w-3 h-3" />{e.parent_email}
                      </a>
                    )}
                  </div>
                </div>
                {e.whatsapp_link && (
                  <button
                    onClick={() => handleWhatsApp(e)}
                    className="bg-[#25D366] hover:bg-[#1faa52] text-white text-xs font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5 flex-shrink-0"
                    data-testid={`whatsapp-btn-${e.id}`}
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    Message on WhatsApp
                  </button>
                )}
                <button
                  onClick={() => dismissFromOverview(e.id)}
                  className="text-white/30 hover:text-white/80 flex-shrink-0 p-1"
                  title="Remove from overview (stays in Free Trials tab)"
                  data-testid={`dismiss-enquiry-${e.id}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2.5">
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                  <button
                    key={key}
                    onClick={() => updateStatus(e.id, key)}
                    className={`text-[11px] px-2 py-1 rounded-full border transition-all ${
                      e.lead_status === key
                        ? cfg.className + ' font-semibold'
                        : 'bg-transparent text-white/40 border-[#333] hover:border-white/40'
                    }`}
                    data-testid={`status-${key}-${e.id}`}
                  >
                    {cfg.label}
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};

export default RecentTrialEnquiriesPanel;
